import i18n from "./i18n";
import "./yup.config";

import {
  getLaunchDarklyRegion,
  getRegionKey,
  RegionContext,
  RegionProvider,
} from "@certn/utils";
import * as Sentry from "@sentry/react";
import { useFlags, withLDProvider } from "launchdarkly-react-client-sdk";
import React from "react";
import ReactDOM from "react-dom/client";
import {
  BrowserRouter,
  createRoutesFromChildren,
  matchRoutes,
  useLocation,
  useNavigationType,
} from "react-router-dom";

import constants from "../constants";
import { Providers } from "./providers";
import { BaseRoutes } from "./routes/BaseRoutes";
import { AppErrorBoundary } from "./components/AppErrorBoundary";
import { ChatBotWrapper } from "@certn/auth";
import { syncHtmlDocumentLanguage } from "./utils/syncHtmlDocumentLanguage";

import "./index.css";

syncHtmlDocumentLanguage(i18n.resolvedLanguage || i18n.language);
i18n.on("languageChanged", (language) =>
  syncHtmlDocumentLanguage(i18n.resolvedLanguage || language)
);

Sentry.init({
  dsn: `https://c31a59a0c2a446514a02981dd6fa37ea@o132497.ingest.sentry.io/4506027393810432`,
  environment: constants.VITE_SENTRY_ENVIRONMENT,
  release: constants.VITE_SENTRY_RELEASE,
  enabled: !!constants.VITE_SENTRY_ENVIRONMENT,
  autoSessionTracking: true,
  ignoreErrors: [
    "ResizeObserver loop",
    "Non-Error exception captured with keys: _pendo",
    /^Non-Error promise rejection captured with value: Object Not Found Matching Id:.*?, MethodName:.*?, ParamCount:.*?/,
  ],
  integrations: [
    Sentry.thirdPartyErrorFilterIntegration({
      filterKeys: ["certn-client-portal"],
      behaviour: "apply-tag-if-exclusively-contains-third-party-frames",
    }),
    Sentry.reactRouterV6BrowserTracingIntegration({
      useEffect: React.useEffect,
      useLocation,
      useNavigationType,
      createRoutesFromChildren,
      matchRoutes,
    }),
  ],
  tracesSampleRate: 0.2,
  tracePropagationTargets: ["localhost", constants.DOMAIN],
  allowUrls: [/https:\/\/.+?\.certn\.co/],
});

if (constants.VITE_SENTRY_PULL_REQUEST) {
  Sentry.setTags({ pull_request: constants.VITE_SENTRY_PULL_REQUEST });
}

function App() {
  const {
    clientPortalAndApplicantPortalEnableAdaChatbot,
    clientPortalAndApplicantPortalEnableAdaChatbotInAus,
  } = useFlags();
  const region = getRegionKey();

  const isChatbotEnabledForRegion =
    region.toLowerCase() === "au"
      ? clientPortalAndApplicantPortalEnableAdaChatbotInAus
      : clientPortalAndApplicantPortalEnableAdaChatbot;

  return isChatbotEnabledForRegion ? (
    <ChatBotWrapper
      currentEnvironment={constants.MODE}
      currentAppName="client-portal"
      chatAlign="right"
    >
      <BaseRoutes />
    </ChatBotWrapper>
  ) : (
    <BaseRoutes />
  );
}

const AppWithLDProvider = constants.VITE_LAUNCH_DARKLY_KEY
  ? withLDProvider({
      clientSideID: constants.VITE_LAUNCH_DARKLY_KEY,
      context: {
        kind: "user",
        key: "anonymous-user-id",
        anonymous: true,
        is_client: true,
        region: getLaunchDarklyRegion(),
      },
    })(App)
  : App;

ReactDOM.createRoot(document.getElementById("root") as HTMLElement).render(
  <React.StrictMode>
    <RegionProvider>
      <RegionContext.Consumer>
        {(region) => (
          <BrowserRouter basename={region}>
            <Providers>
              <AppErrorBoundary>
                <AppWithLDProvider />
              </AppErrorBoundary>
            </Providers>
          </BrowserRouter>
        )}
      </RegionContext.Consumer>
    </RegionProvider>
  </React.StrictMode>
);
